import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Icon } from '@/common/components/Icon'
import { PageTitle } from '@/components'
import { Button, Input, Modal } from '@/components/ui'
import SelectField from '@/components/select-field'
import FormGroup from '@/core/components/FormGroup'
import {
  DEPLETION_POLICIES,
  DepletionPolicy,
  PartnerDto,
  RewardPoolDto,
  RewardPoolUpsertDto,
  RoadmapDto,
} from '@/common/models/gamification'

import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { translationTitle } from './shared/labels'

interface CreateRewardPoolModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  pool?: RewardPoolDto | null
  partners: PartnerDto[]
  roadmaps: RoadmapDto[]
  onSubmit: (values: RewardPoolUpsertDto, id?: string) => void
  onDelete?: (id: string) => void
}

interface IRewardPoolFormValues {
  depletionPolicy: DepletionPolicy
  partnerId: string
  roadmapId: string
  totalQty?: number
}

const POLICY_LABELS: Record<DepletionPolicy, string> = {
  BLOCK: 'Block',
  UNLIMITED: 'Unlimited',
}

export const CreateRewardPoolModal = ({
  open,
  onOpenChange,
  pool,
  partners,
  roadmaps,
  onSubmit,
  onDelete,
}: CreateRewardPoolModalProps) => {
  const isEditMode = !!pool
  const [deleteOpen, setDeleteOpen] = useState(false)

  const schema = yup.object().shape({
    depletionPolicy: yup
      .string()
      .oneOf(DEPLETION_POLICIES, 'Policy is required')
      .required('Policy is required'),
    partnerId: yup.string().default(''),
    roadmapId: yup.string().default(''),
    totalQty: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === '' || Number.isNaN(value) ? undefined : value,
      )
      .when('depletionPolicy', {
        is: 'BLOCK',
        then: (s) =>
          s
            .typeError('Quantity must be a number')
            .required('Total quantity is required')
            .min(0, 'Must be 0 or greater'),
        otherwise: (s) => s.notRequired(),
      }),
  })

  const defaultValues: IRewardPoolFormValues = {
    depletionPolicy: 'BLOCK',
    partnerId: '',
    roadmapId: '',
    totalQty: undefined,
  }

  const {
    handleSubmit,
    register,
    control,
    watch,
    formState: { errors, dirtyFields },
    reset,
    setValue,
  } = useForm<IRewardPoolFormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema) as never,
    defaultValues,
  })

  const depletionPolicy = watch('depletionPolicy')

  useEffect(() => {
    if (!open) return
    if (pool) {
      setValue('depletionPolicy', pool.depletionPolicy, { shouldValidate: true })
      setValue('partnerId', pool.partnerId ?? '', { shouldValidate: true })
      setValue('roadmapId', pool.roadmapId ?? '', { shouldValidate: true })
      setValue('totalQty', pool.totalQty ?? undefined, { shouldValidate: true })
    }
    return () => {
      reset(defaultValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pool])

  const handleClose = () => onOpenChange(false)

  const partnerOptions = [
    { value: 'none', label: 'None' },
    ...partners.map((p) => ({ value: p.id, label: p.name })),
  ]

  const roadmapOptions = [
    { value: 'none', label: 'None' },
    ...roadmaps.map((r) => ({
      value: r.id,
      label: translationTitle(r.translations, `${r.scope} roadmap`),
    })),
  ]

  const submit = (values: IRewardPoolFormValues) => {
    onSubmit(
      {
        depletionPolicy: values.depletionPolicy,
        partnerId: values.partnerId || undefined,
        roadmapId: values.roadmapId || undefined,
        totalQty:
          values.depletionPolicy === 'BLOCK'
            ? values.totalQty
            : (values.totalQty ?? undefined),
      },
      pool?.id,
    )
    handleClose()
  }

  return (
    <>
      <Modal
        open={open}
        onOpenChange={(o) => {
          if (!o) handleClose()
        }}
        size="md"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              icon="gift"
              title={
                <span className="flex items-center gap-2">
                  {isEditMode ? 'Edit' : 'Add New'}{' '}
                  <span className="text-accent-1">Reward Pool</span>
                </span>
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {isEditMode && onDelete ? (
              <Button
                onClick={() => setDeleteOpen(true)}
                variant="destructive"
                className="mr-auto"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                disabled={isEditMode && Object.keys(dirtyFields).length === 0}
                form="create-reward-pool-form"
                type="submit"
                variant="primary"
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="create-reward-pool-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-6"
        >
          <Controller
            name="depletionPolicy"
            control={control}
            render={({ field }) => (
              <SelectField
                label="Depletion Policy"
                required
                placeholder="Select policy"
                error={errors?.depletionPolicy?.message}
                options={DEPLETION_POLICIES.map((p) => ({
                  value: p,
                  label: POLICY_LABELS[p],
                }))}
                onValueChange={field.onChange}
                {...field}
              />
            )}
          />

          <Controller
            name="partnerId"
            control={control}
            render={({ field }) => (
              <SelectField
                label="Partner"
                placeholder="None"
                options={partnerOptions}
                value={field.value || 'none'}
                onValueChange={(v) => field.onChange(v === 'none' ? '' : v)}
              />
            )}
          />

          <Controller
            name="roadmapId"
            control={control}
            render={({ field }) => (
              <SelectField
                label="Roadmap"
                placeholder="None"
                options={roadmapOptions}
                value={field.value || 'none'}
                onValueChange={(v) => field.onChange(v === 'none' ? '' : v)}
              />
            )}
          />

          <FormGroup
            label="Total Qty"
            error={errors?.totalQty?.message}
          >
            <Input
              isInvalid={!!errors?.totalQty?.message}
              {...register('totalQty', { valueAsNumber: true })}
              type="number"
              placeholder={depletionPolicy === 'UNLIMITED' ? '∞' : '0'}
            />
          </FormGroup>

          {isEditMode && (
            <div className="text-body-medium text-monochrome-2">
              Consumed: <span className="text-monochrome-1">{pool?.consumed}</span>
            </div>
          )}
        </form>
      </Modal>

      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteOpen}
        setDeleteConfirmationModal={setDeleteOpen}
        deletedItem="reward pool"
        deleteFunc={() => {
          if (pool && onDelete) onDelete(pool.id)
          handleClose()
        }}
      />
    </>
  )
}
